import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/config";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { Loader2, Sparkles, Leaf, Trash2, Navigation, Wrench, Box, Repeat, CalendarDays, CalendarRange, Calendar as CalendarIcon, Home as HomeIcon, Trees, MapPin, ChevronDown, ChevronUp, Lock, Unlock, ShieldCheck, Plus, Info } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AddressAutocomplete from "./AddressAutocomplete";
import CustomerPropertyPreview from "./CustomerPropertyPreview";
import PropertyVerifyingCard from "./PropertyVerifyingCard";
import MakeOfferDialog from "./MakeOfferDialog";
import QuoteRangeBuilder from "./QuoteRangeBuilder";
import AreaSelector from "./AreaSelector";
import GateWidthPicker from "./GateWidthPicker";
import { useAuth } from "../contexts/AuthContext";

// iter126 — Derive the legacy `yard_area` (front|back|both) from the
// canonical multi-area list so the FL-parcel auto-detect logic that depends
// on front_yard_sqft / back_yard_sqft keeps working. `both` covers every
// case with 2+ areas or "entire_property".
function deriveYardArea(areas) {
  if (!Array.isArray(areas) || areas.length === 0) return "both";
  if (areas.length === 1 && areas[0] === "front_yard") return "front";
  if (areas.length === 1 && areas[0] === "back_yard") return "back";
  return "both";
}

const RANGE_SERVICE_KEYS = new Set(["hedge_trimming", "yard_cleanup", "mulching", "landscaping"]);

const TOGGLES = [
  { key: "heavy_debris", label: "Heavy debris", desc: "Branches, leaves, trash (+$25)", Icon: Trash2 },
  { key: "severe_debris", label: "Severe debris", desc: "Bumps haul-off to +$50", Icon: Trash2 },
  { key: "corner_lot", label: "Corner lot", desc: "More edging needed (+$10)", Icon: Navigation },
  { key: "difficult_labor", label: "Difficult labor", desc: "Slopes, obstacles (+20%)", Icon: Wrench },
];

const GRASS_HEIGHTS = [
  { key: "under_9",  label: "Under 9 in.", desc: "Normal upkeep",  pct: 0,   tone: "save" },
  { key: "9_to_15",  label: "9–15 in.",    desc: "Tall growth",    pct: 100, tone: "addon" },
  { key: "16_to_21", label: "16–21 in.",   desc: "Very overgrown", pct: 300, tone: "addon" },
  { key: "22_plus",  label: "22+ in.",     desc: "Field-like",     pct: 450, tone: "addon" },
];

const GRASS_COVERAGES = [
  { key: "partial", label: "Partial area", desc: "Some patches",  factor: 0.5 },
  { key: "full",    label: "Full area",    desc: "Whole yard",    factor: 1.0 },
];

// Customer-facing yard size categories (square footage is hidden from
// customers to prevent fraud and confusion). Admins continue to see the
// exact number with the override toggle.
const YARD_CATEGORIES = [
  { key: "small",       label: "Small Yard",       max: 4000,   desc: "Under 4,000 sq ft" },
  { key: "medium",      label: "Medium Yard",      max: 8000,   desc: "4,000 – 8,000 sq ft" },
  { key: "large",       label: "Large Yard",       max: 15000,  desc: "8,000 – 15,000 sq ft" },
  { key: "extra_large", label: "Extra Large Yard", max: Infinity, desc: "Over 15,000 sq ft" },
];
function yardCategoryFor(sqft) {
  const n = Number(sqft || 0);
  if (n <= 0) return null;
  return YARD_CATEGORIES.find((c) => n < c.max) || YARD_CATEGORIES[YARD_CATEGORIES.length - 1];
}

const FREQUENCIES = [
  { key: "weekly",         label: "Weekly",         badge: "Best Rate",  tone: "save",    Icon: Repeat },
  { key: "biweekly",       label: "Every 2 Weeks",  badge: "Popular",    tone: "save",    Icon: CalendarRange },
  { key: "every_3_weeks",  label: "Every 3 Weeks",  badge: null,         tone: "default", Icon: CalendarRange },
  { key: "every_4_weeks",  label: "Every 4 Weeks",  badge: null,         tone: "default", Icon: CalendarDays },
  { key: "monthly",        label: "Monthly",        badge: null,         tone: "default", Icon: CalendarDays },
  { key: "every_6_weeks",  label: "Every 6 Weeks",  badge: null,         tone: "default", Icon: CalendarDays },
  { key: "one_time",       label: "One-Time",       badge: "Highest",    tone: "addon",   Icon: CalendarIcon },
];

const YARD_AREAS = [
  { key: "front", label: "Front Yard",  badge: null, tone: "save",    Icon: HomeIcon, desc: "Just the front" },
  { key: "back",  label: "Back Yard",   badge: null, tone: "save",    Icon: Trees,    desc: "Just the back" },
  { key: "both",  label: "Front + Back", badge: "Full",    tone: "default", Icon: Sparkles, desc: "Whole yard" },
];

export default function EstimateCalculator({ services, defaultService, defaultFrequency = "biweekly", defaultYardArea = "both", defaultSqft = 2000, compact = false, onContinue }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!user?.is_admin;
  const [serviceKey, setServiceKey] = useState(defaultService || services?.[0]?.key || "lawn_mowing");
  const [sqft, setSqft] = useState(defaultSqft);
  const [frequency, setFrequency] = useState(defaultFrequency);
  const [yardArea, setYardArea] = useState(defaultYardArea);
  // iter126 — Shared multi-area selection. `yardArea` is now derived from
  // this list (see deriveYardArea) so the FL-parcel auto-detect keeps working.
  const [areas, setAreas] = useState(
    defaultYardArea === "front" ? ["front_yard"]
    : defaultYardArea === "back" ? ["back_yard"]
    : ["front_yard", "back_yard"]
  );
  // Keep yardArea in lockstep with the customer's map selection.
  useEffect(() => {
    setYardArea(deriveYardArea(areas));
  }, [areas]);
  const [opts, setOpts] = useState({
    heavy_debris: false, severe_debris: false, corner_lot: false, difficult_labor: false,
  });
  // iter135 — Gate width required so the crew knows the backyard access
  const [gateWidth, setGateWidth] = useState("");
  // New overgrown model — height + coverage drives a % surcharge on labor.
  const [grassHeight, setGrassHeight] = useState("under_9");
  const [grassCoverage, setGrassCoverage] = useState("full");
  // Admin-only — override the auto-calculated overgrown % (e.g. 1.25 = +125%).
  const [overgrownOverride, setOvergrownOverride] = useState("");  // string for input control
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [property, setProperty] = useState(null); // {formatted_address, acres, square_feet, source}
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [adminOverride, setAdminOverride] = useState(false);
  // Quote-range data — populated by QuoteRangeBuilder when serviceKey is a range service.
  const [rangeData, setRangeData] = useState(null);

  // Recurring lawn-mowing pricing preview (per cadence) — refreshes whenever
  // sqft / yard_area / areas change. Powers the in-tile per-visit dollar
  // amounts + savings message above the frequency picker. iter126 — passes
  // the canonical `areas` CSV so the tier cards reflect the multi-area
  // multiplier (3+ areas / Entire Property).
  const [recurringPricing, setRecurringPricing] = useState({ tiers: [], savings_message: "" });
  useEffect(() => {
    if (serviceKey !== "lawn_mowing" || !sqft || sqft < 1) {
      setRecurringPricing({ tiers: [], savings_message: "" });
      return;
    }
    let cancelled = false;
    const areasCsv = (areas || []).join(",");
    // iter141 — pass mowable_sqft so the tier cards reflect the exact
    // progressive (linear-interpolation) price the customer will pay.
    const mowable = property?.mowable_sqft || property?.square_feet || sqft;
    api.get(`/lawn-mowing/recurring-pricing?square_feet=${sqft}&yard_area=${yardArea}&areas=${encodeURIComponent(areasCsv)}&mowable_sqft=${mowable}`)
      .then(({ data }) => { if (!cancelled) setRecurringPricing(data || { tiers: [], savings_message: "" }); })
      .catch(() => { if (!cancelled) setRecurringPricing({ tiers: [], savings_message: "" }); });
    return () => { cancelled = true; };
  }, [serviceKey, sqft, yardArea, areas, property?.mowable_sqft, property?.square_feet]);
  const priceByFreq = Object.fromEntries((recurringPricing.tiers || []).map(t => [t.frequency, t.price_per_visit]));

  // Lock the sqft field once the FL Parcels API returns data.
  // Admins can toggle adminOverride to bypass the lock. The yard-area picker
  // (front/back/both) stays unlocked — selecting an area swaps the locked
  // sqft to that area's detected size so pricing uses the *real* measurement.
  const isLocked = autoFilled && !adminOverride;

  // Helper — pick the detected sqft for a given yard area when property is auto-filled.
  const detectedSqftFor = (area) => {
    if (!property) return null;
    if (area === "front" && property.front_yard_sqft) return property.front_yard_sqft;
    if (area === "back" && property.back_yard_sqft) return property.back_yard_sqft;
    if (area === "both" && property.square_feet) return property.square_feet;
    return null;
  };

  // Swap displayed sqft to the area's detected size whenever the user picks
  // a different yard area AND property is auto-detected.
  useEffect(() => {
    if (!autoFilled || adminOverride) return;
    const target = detectedSqftFor(yardArea);
    if (target && target !== sqft) {
      setSqft(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yardArea, autoFilled, adminOverride, property]);

  const handleAddressSelect = async (place) => {
    if (!place?.formatted_address) return;
    // Reset admin override whenever a new address is selected so the lock
    // re-engages by default.
    setAdminOverride(false);
    if (!place.lat || !place.lng) {
      setProperty({ formatted_address: place.formatted_address });
      setAutoFilled(false);
      // Clear the default sqft so the user must explicitly enter or pick one
      setSqft(0);
      return;
    }
    setPropertyLoading(true);
    // iter153 — Set an instant preview with the just-selected coords so
    // the PropertyVerifyingCard can render the satellite tile the moment
    // the loading overlay appears (before /property/lookup returns).
    setProperty({
      formatted_address: place.formatted_address,
      lat: place.lat,
      lng: place.lng,
    });

    // iter153 — Skip the duplicate /property/lookup when AddressAutocomplete
    // already forwarded the enriched payload (square_feet, mowable_sqft, etc.
    // via its own upstream call). Saves a ~500 ms round-trip and shortens
    // the loading overlay to the actual pricing wait.
    if (place.square_feet && place.square_feet > 0) {
      setProperty({ ...place, formatted_address: place.formatted_address });
      setSqft(Math.min(50000, Math.max(0, place.square_feet)));
      setAutoFilled(true);
      setPropertyLoading(false);
      return;
    }

    try {
      const { data } = await api.post("/property/lookup", {
        lat: place.lat,
        lng: place.lng,
        address: place.formatted_address,
      });
      setProperty({ ...data, formatted_address: place.formatted_address });
      if (data?.square_feet && data.square_feet > 0) {
        setSqft(Math.min(50000, Math.max(0, data.square_feet)));
        setAutoFilled(true);
      } else {
        setAutoFilled(false);
        setSqft(0);
      }
    } catch (e) {
      setProperty({ formatted_address: place.formatted_address });
      setAutoFilled(false);
      setSqft(0);
    } finally {
      setPropertyLoading(false);
    }
  };

  const payload = useMemo(() => {
    const overrideNum = parseFloat(overgrownOverride);
    const overrideValid = isAdmin && !isNaN(overrideNum) && overrideNum >= 0;
    return {
      service_key: serviceKey,
      square_feet: sqft,
      // iter142 — pass mowable_sqft so this endpoint uses the SAME grass-area
      // input as /lawn-mowing/recurring-pricing. Fixes the "$59 tier card vs
      // $61.23 main total" mismatch caused by one endpoint using mowable and
      // the other using the total lot sqft.
      mowable_sqft: property?.mowable_sqft ?? null,
      frequency,
      yard_area: yardArea,
      areas,  // iter126 — canonical multi-area selection
      area_already_scaled: autoFilled && !adminOverride,
      grass_height: grassHeight,
      grass_coverage: grassCoverage,
      overgrown_override_pct: overrideValid ? overrideNum / 100 : null,
      ...opts,
    };
  }, [serviceKey, sqft, frequency, yardArea, areas, opts, autoFilled, adminOverride, grassHeight, grassCoverage, overgrownOverride, isAdmin, property?.mowable_sqft]);

  useEffect(() => {
    // Skip the standard estimate calc for range services — they use /quotes/range
    // via QuoteRangeBuilder instead.
    if (RANGE_SERVICE_KEYS.has(serviceKey)) {
      setCalc(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/estimates/calculate", payload);
        if (active) setCalc(data);
      } catch (e) { /* noop */ }
      finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [payload, serviceKey]);

  const handleSqftChange = (val) => {
    if (isLocked) return;
    const n = parseInt(String(val).replace(/[^0-9]/g, "")) || 0;
    setSqft(Math.min(50000, Math.max(0, n)));
  };

  // iter136 — Gate the customer flow until the pricing engine is fully
  // ready. For customers we require: property verified (autoFilled=true
  // via /property/lookup), calc has run and calc.total > 0, and no
  // in-flight requests. Admins can proceed at any time (they may be
  // seeding a manual quote without a real address).
  const propertyVerified = autoFilled === true && !!(property?.lat && property?.lng);
  const isBusy = loading || propertyLoading;
  const priceReady =
    isAdmin
      ? (!isBusy && !!calc && Number(calc?.total || 0) > 0)
      : (!isBusy && propertyVerified && !!calc && Number(calc?.total || 0) > 0);
  // iter136 — Blocking overlay while property lookup or pricing is
  // in flight for a non-admin customer. Show it as soon as the user
  // begins the lookup (propertyLoading) OR when calc is in flight
  // even before we have a formatted_address, so customers never
  // interact with a half-loaded state.
  const showFullScreenOverlay = !isAdmin && isBusy && !priceReady && (
    propertyLoading || !!(property?.formatted_address)
  );

  return (
    <div className="space-y-6" data-testid="estimate-calculator">
      {/* iter153 — Rich property-verifying experience (replaces the plain
          spinner). Renders satellite tile, animated progress, cycling
          status messages, and nearby-customer trust signals while lookup
          + pricing runs. */}
      <PropertyVerifyingCard property={property} visible={showFullScreenOverlay} />
      {/* ADDRESS AUTOCOMPLETE — auto-detect lot size */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">Your address</div>
        <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 text-[#047857] flex items-center justify-center shrink-0">
              <MapPin className="w-4.5 h-4.5" />
            </div>
            <div className="flex-1 min-w-0">
              <AddressAutocomplete
                placeholder="Enter your service address"
                onPlace={handleAddressSelect}
                showInlineSummary={false}
                data-testid="calc-address-input"
              />
              {propertyLoading && (
                <div className="mt-2 text-[12px] text-gray-500 flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Detecting property…
                </div>
              )}
              {property?.formatted_address && !propertyLoading && (
                <div className="mt-2">
                  {/* iter43 — Customer-facing satellite property preview */}
                  {!isAdmin && property?.lat && property?.lng && (
                    <CustomerPropertyPreview property={property} />
                  )}
                  <div className="flex items-center gap-2 flex-wrap text-[11px] mt-2">
                    <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-[#047857] border border-emerald-200 font-bold">
                      {property.formatted_address}
                    </span>
                    {autoFilled && property?.acres && isAdmin && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-[#047857] font-extrabold uppercase tracking-wider">
                        Auto-detected · ~{property.acres} ac{property.square_feet ? ` · ${property.square_feet.toLocaleString()} sqft` : ""}
                      </span>
                    )}
                    {/* iter86: Hide the "Lot size unavailable — please enter manually"
                        warning from customers. Booking continues seamlessly using the
                        current sqft/frequency/yard-area inputs; the backend flags the
                        booking (`lot_size_source: "customer_default"`) so admin can
                        review lot data during scheduling. Admins still see the pill
                        so they know parcel data didn't hit. */}
                    {!autoFilled && property?.formatted_address && isAdmin && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-bold" data-testid="lot-unavailable-admin">
                        Lot size unavailable — flagged for review
                      </span>
                    )}
                  </div>
                  {autoFilled && property?.building_footprint_sqft && isAdmin && (
                    <button
                      type="button"
                      onClick={() => setShowDetails((s) => !s)}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#047857] font-bold hover:underline"
                      data-testid="property-details-toggle"
                    >
                      {showDetails ? <><ChevronUp className="w-3.5 h-3.5" /> Hide property breakdown</> : <><ChevronDown className="w-3.5 h-3.5" /> Show property breakdown</>}
                    </button>
                  )}
                  {showDetails && autoFilled && property?.building_footprint_sqft && isAdmin && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="property-stats">
                      <PropertyStat label="Lot size" value={`${property.square_feet?.toLocaleString()} sqft`} sub={`${property.acres} ac`} />
                      <PropertyStat label="Building (est.)" value={`${property.building_footprint_sqft?.toLocaleString()} sqft`} sub="~22% of lot" />
                      <PropertyStat label="Front yard" value={`${property.front_yard_sqft?.toLocaleString()} sqft`} sub="~45% of yard" />
                      <PropertyStat label="Back yard" value={`${property.back_yard_sqft?.toLocaleString()} sqft`} sub="~55% of yard" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* SERVICE PILL TABS */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">Service</div>
        <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1 snap-x" style={{ scrollbarWidth: "none" }}>
          {services?.map((s) => {
            const active = serviceKey === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setServiceKey(s.key)}
                data-testid={`calc-service-${s.key}`}
                className={`shrink-0 snap-start h-12 px-5 rounded-full font-bold text-[15px] flex items-center gap-2 transition-all ${
                  active
                    ? "bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white shadow-md shadow-black/10"
                    : "bg-white text-gray-700 border border-gray-200 hover:border-gray-300"
                }`}
              >
                <Leaf className={`w-4 h-4 ${active ? "text-emerald-300" : "text-[#10B981]"}`} />
                {s.name.replace(" / ", "/")}
              </button>
            );
          })}
        </div>
        {/* iter117 — Lawn Mowing inclusions callout. Shown ONLY when
            the Lawn Mowing service is selected. */}
        {serviceKey === "lawn_mowing" && (
          <div
            className="mt-3 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-3"
            data-testid="lawn-mowing-inclusions-info"
            role="note"
          >
            <div className="w-8 h-8 shrink-0 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Info className="w-4 h-4" />
            </div>
            <div className="text-[12.5px] leading-relaxed text-emerald-950">
              <span className="font-extrabold text-emerald-900">Included with Lawn Mowing:</span>{" "}
              Grass mowing, edging, weed trimming, and blowing are all included in this service.
            </div>
          </div>
        )}
      </div>

      {/* RANGE BUILDER — replaces the standard calculator for quote-only services */}
      {RANGE_SERVICE_KEYS.has(serviceKey) && (
        <QuoteRangeBuilder
          serviceKey={serviceKey}
          onSubmit={(range) => {
            if (!range) return;
            setRangeData(range);
            setOfferOpen(true);
          }}
        />
      )}

      {/* iter127 — Yard Area picker. Restored the 3-card design per customer
          feedback (Front / Back / Front + Back). AreaSelector renders its own
          "YARD AREA" section label. */}
      {!RANGE_SERVICE_KEYS.has(serviceKey) && (<>
      <div data-testid="yard-area-picker">
        <AreaSelector value={areas} onChange={setAreas} />
      </div>

      {/* iter135 — Gate Width so the crew knows backyard access */}
      <div data-testid="gate-width-wrap">
        <GateWidthPicker value={gateWidth} onChange={setGateWidth} required />
      </div>

      {/* SERVICE FREQUENCY */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold">Service Frequency</div>
          {recurringPricing.savings_message && serviceKey === "lawn_mowing" && (
            <span className="text-[10px] sm:text-[11px] font-bold text-[#047857]" data-testid="recurring-savings-mini">
              Weekly = best rate
            </span>
          )}
        </div>
        {recurringPricing.savings_message && serviceKey === "lawn_mowing" && (
          <div
            className="mb-3 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/40 px-3.5 py-2.5 flex items-start gap-2.5"
            data-testid="recurring-savings-banner"
          >
            <Repeat className="w-4 h-4 text-[#047857] mt-0.5 shrink-0" />
            <p className="text-[12px] sm:text-[13px] leading-snug text-emerald-900 font-medium">
              {recurringPricing.savings_message}
            </p>
          </div>
        )}
        <div className={`grid ${serviceKey === "lawn_mowing" ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"} gap-2.5`} data-testid="frequency-picker">
          {FREQUENCIES.map((f) => {
            const active = frequency === f.key;
            const tilePrice = serviceKey === "lawn_mowing" ? priceByFreq[f.key] : null;
            const toneClasses = active
              ? "bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white border-transparent shadow-md shadow-black/10"
              : "bg-white text-gray-800 border-gray-200 hover:border-gray-300";
            const badgeClasses = active
              ? f.tone === "save"
                ? "bg-emerald-300/25 text-emerald-200"
                : f.tone === "addon"
                  ? "bg-amber-200/25 text-amber-200"
                  : "bg-white/15 text-emerald-100"
              : f.tone === "save"
                ? "bg-emerald-100 text-[#047857]"
                : f.tone === "addon"
                  ? "bg-amber-100 text-amber-700"
                  : "bg-gray-100 text-gray-600";
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFrequency(f.key)}
                data-testid={`frequency-${f.key}`}
                className={`relative text-left rounded-2xl border p-4 transition-all active:scale-[0.98] ${toneClasses}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <f.Icon className={`w-4.5 h-4.5 ${active ? "text-emerald-300" : "text-[#10B981]"}`} />
                  {f.badge && (
                    <span className={`text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${badgeClasses}`}>
                      {f.badge}
                    </span>
                  )}
                </div>
                <div className="mt-2 text-[14px] sm:text-[15px] font-extrabold tracking-tight font-[Outfit] leading-tight">{f.label}</div>
                {tilePrice != null && (
                  <div
                    className={`mt-1.5 text-[11px] font-extrabold tracking-tight ${active ? "text-emerald-200" : "text-[#047857]"}`}
                    data-testid={`frequency-price-${f.key}`}
                  >
                    {/* iter142 — cents precision so the tier card matches the
                        main total exactly (no more $59 tile → $58.96 total). */}
                    ${Number(tilePrice).toFixed(2)}<span className={`ml-0.5 text-[10px] font-bold opacity-80`}>/visit</span>
                  </div>
                )}
                {active && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-emerald-400 via-emerald-300 to-emerald-400 rounded-b-2xl" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* YARD SIZE — admins only. Customers never see size / sqft / category
          per iter42 spec: pricing is calculated silently from the verified
          mowable area, but no measurements are shown to the customer. */}
      {isAdmin && (
        <div>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold">Yard Size (sq ft)</div>
            <div className="flex items-center gap-2">
              {autoFilled && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-emerald-600 text-white shadow-sm" data-testid="sqft-auto-detected-badge">
                  {isLocked ? <Lock className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />} {yardArea === "front" ? "Front yard" : yardArea === "back" ? "Back yard" : "Full yard"} · Auto-Detected
                </span>
              )}
              {autoFilled && (
                <button
                  type="button"
                  onClick={() => setAdminOverride((v) => !v)}
                  data-testid="admin-sqft-override-btn"
                  className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-1 rounded-full border transition-colors ${
                    adminOverride
                      ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600"
                      : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"
                  }`}
                >
                  {adminOverride ? <><Unlock className="w-3 h-3" /> Override active</> : <><Lock className="w-3 h-3" /> Override (admin)</>}
                </button>
              )}
            </div>
          </div>
          <div className={`bg-white rounded-2xl border px-5 h-16 flex items-center gap-3 ${isLocked ? "border-emerald-300 bg-emerald-50/50" : "border-gray-200"}`} data-testid="calc-sqft-row">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isLocked ? "bg-emerald-600 text-white" : "bg-emerald-50 text-[#047857]"}`}>
              {isLocked ? <Lock className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
            </div>
            <input
              inputMode="numeric"
              value={sqft}
              onChange={(e) => handleSqftChange(e.target.value)}
              onKeyDown={(e) => { if (isLocked) e.preventDefault(); }}
              readOnly={isLocked}
              disabled={isLocked}
              aria-readonly={isLocked}
              data-testid="calc-sqft-input"
              className={`flex-1 text-2xl font-extrabold tracking-tight tabular-nums bg-transparent outline-none ${isLocked ? "text-[#047857] cursor-not-allowed select-none" : "text-gray-900"}`}
            />
            <div className="text-sm font-bold text-gray-400 uppercase tracking-wider">sq ft</div>
          </div>
          {isLocked ? (
            <div className="mt-2 text-[11px] text-gray-600 flex items-center gap-1.5" data-testid="sqft-lock-message">
              <Lock className="w-3 h-3 text-emerald-600" />
              <span>{yardArea === "both" ? "Full lot size" : `${yardArea === "front" ? "Front" : "Back"} yard portion`} pulled from verified Florida property records. Tap Override to edit.</span>
            </div>
          ) : (
            <div className="mt-2 flex gap-2">
              {[1000, 2500, 5000, 10000].map((v) => (
                <button key={v} type="button" onClick={() => setSqft(v)} data-testid={`calc-sqft-quick-${v}`}
                  className={`px-3 h-8 rounded-full text-xs font-bold border ${sqft === v ? "border-[#047857] text-[#047857] bg-emerald-50" : "border-gray-200 text-gray-600 bg-white"}`}>
                  {v.toLocaleString()}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* GRASS CONDITION (overgrown surcharge) */}
      <div data-testid="grass-condition-section">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold">Grass Condition</div>
          {(() => {
            const overrideNum = parseFloat(overgrownOverride);
            const overrideValid = isAdmin && !isNaN(overrideNum) && overrideNum >= 0;
            const heightPct = GRASS_HEIGHTS.find((h) => h.key === grassHeight)?.pct || 0;
            const covFactor = GRASS_COVERAGES.find((c) => c.key === grassCoverage)?.factor || 1.0;
            const pct = overrideValid ? overrideNum : Math.round(heightPct * covFactor);
            if (pct === 0) return null;
            return (
              <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-amber-500 text-white shadow-sm" data-testid="grass-surcharge-badge">
                +{pct}% overgrown
              </span>
            );
          })()}
        </div>

        {/* Grass height picker */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="grass-height-picker">
          {GRASS_HEIGHTS.map((h) => {
            const active = grassHeight === h.key;
            const toneClasses = active
              ? "bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white border-transparent shadow-md shadow-black/10"
              : "bg-white text-gray-800 border-gray-200 hover:border-gray-300";
            const badgeClasses = active
              ? h.tone === "save"
                ? "bg-emerald-300/25 text-emerald-200"
                : h.tone === "addon"
                  ? "bg-amber-200/25 text-amber-100"
                  : "bg-white/15 text-emerald-100"
              : h.tone === "save"
                ? "bg-emerald-100 text-[#047857]"
                : h.tone === "addon"
                  ? "bg-amber-100 text-amber-700"
                  : "bg-gray-100 text-gray-600";
            return (
              <button
                key={h.key}
                type="button"
                onClick={() => setGrassHeight(h.key)}
                data-testid={`grass-height-${h.key}`}
                className={`relative text-left rounded-2xl border p-3 transition-all active:scale-[0.98] ${toneClasses}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <Leaf className={`w-4 h-4 ${active ? "text-emerald-300" : "text-[#10B981]"}`} />
                  <span className={`text-[9px] font-extrabold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full ${badgeClasses}`}>
                    {h.pct === 0 ? "Normal" : `+${h.pct}%`}
                  </span>
                </div>
                <div className="mt-2 text-[13px] font-extrabold tracking-tight font-[Outfit] leading-tight">{h.label}</div>
                <div className={`mt-0.5 text-[11px] ${active ? "text-emerald-100/80" : "text-gray-500"}`}>{h.desc}</div>
              </button>
            );
          })}
        </div>

        {/* Coverage — only relevant if height > under_9 */}
        {grassHeight !== "under_9" && (
          <div className="mt-3 grid grid-cols-2 gap-2.5" data-testid="grass-coverage-picker">
            {GRASS_COVERAGES.map((c) => {
              const active = grassCoverage === c.key;
              const toneClasses = active
                ? "bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white border-transparent shadow-md shadow-black/10"
                : "bg-white text-gray-800 border-gray-200 hover:border-gray-300";
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setGrassCoverage(c.key)}
                  data-testid={`grass-coverage-${c.key}`}
                  className={`relative text-left rounded-2xl border p-3 transition-all active:scale-[0.98] ${toneClasses}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <Sparkles className={`w-4 h-4 ${active ? "text-emerald-300" : "text-[#10B981]"}`} />
                    <span className={`text-[9px] font-extrabold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full ${active ? "bg-white/15 text-emerald-100" : "bg-gray-100 text-gray-600"}`}>
                      ×{c.factor}
                    </span>
                  </div>
                  <div className="mt-2 text-[13px] font-extrabold tracking-tight font-[Outfit] leading-tight">{c.label}</div>
                  <div className={`mt-0.5 text-[11px] ${active ? "text-emerald-100/80" : "text-gray-500"}`}>{c.desc}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Admin override input */}
        {isAdmin && (
          <div className="mt-3 flex items-center gap-2 rounded-2xl border border-amber-300 bg-amber-50 p-3" data-testid="grass-admin-override">
            <Wrench className="w-4 h-4 text-amber-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-amber-800">Admin override</div>
              <div className="text-[11px] text-amber-700/80 leading-snug">Bypass auto-calc with a custom %. Leave blank to use tier above.</div>
            </div>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0"
                step="5"
                placeholder="—"
                value={overgrownOverride}
                onChange={(e) => setOvergrownOverride(e.target.value)}
                data-testid="grass-admin-override-input"
                className="w-16 h-9 px-2 rounded-lg border border-amber-300 bg-white text-sm font-extrabold text-amber-800 tabular-nums text-right outline-none focus:border-amber-500"
              />
              <span className="text-sm font-extrabold text-amber-800">%</span>
            </div>
          </div>
        )}
      </div>

      {/* JOB CONDITIONS */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">Job Conditions</div>
        <div className="space-y-3">
          {TOGGLES.map(({ key, label, desc, Icon }) => (
            <label key={key} className="flex items-center gap-4 bg-white rounded-2xl border border-gray-200 p-4 cursor-pointer hover:border-gray-300 transition-colors" data-testid={`calc-card-${key}`}>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 text-[#047857] flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-bold text-gray-900 font-[Outfit] leading-tight">{label}</div>
                <div className="text-[13px] text-gray-500 mt-0.5">{desc}</div>
              </div>
              <Switch checked={opts[key]} onCheckedChange={(v) => setOpts((o) => ({ ...o, [key]: v }))} data-testid={`calc-toggle-${key}`} />
            </label>
          ))}
        </div>
      </div>

      {/* TOTAL */}
      <div className="rounded-3xl bg-gradient-to-br from-[#0A2E1E] via-[#093924] to-[#062818] text-white p-7 relative overflow-hidden" data-testid="calc-total-card">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="absolute -bottom-12 -left-10 w-44 h-44 rounded-full bg-emerald-300/10 blur-3xl" />
        <div className="relative">
          {/* iter87: Lot > 80,000 sqft → no instant price; steer the customer
              to Request a Quote so we can price with photos / measurements. */}
          {calc?.custom_quote_required ? (
            <div data-testid="calc-custom-quote-required">
              <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Custom quote required</div>
              <div className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tighter font-[Outfit]">
                Let&apos;s price this together
              </div>
              <div className="mt-2 text-sm text-emerald-100/85 leading-relaxed">
                Your property is larger than our instant-pricing tiers cover. Tap below and we&apos;ll send a tailored quote within 24 hours — usually with photos or a quick call.
              </div>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <Link to={`/quote/request?service=lawn_mowing`}>
                  <Button className="rounded-full bg-white hover:bg-emerald-50 text-[#062818] font-extrabold h-12 px-7 shadow-md shadow-black/10" data-testid="calc-custom-quote-btn">
                    Request a Quote →
                  </Button>
                </Link>
              </div>
            </div>
          ) : (<>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Your Estimate</div>
            {calc?.frequency_label && (
              <span data-testid="calc-frequency-pill" className={`text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${calc.frequency_delta < 0 ? "bg-emerald-300/25 text-emerald-200" : calc.frequency_delta > 0 ? "bg-amber-200/25 text-amber-100" : "bg-white/15 text-emerald-100"}`}>
                {FREQUENCIES.find((f) => f.key === calc.frequency)?.label || "Standard"}
              </span>
            )}
          </div>
          <div className="mt-1 text-5xl sm:text-6xl font-extrabold tracking-tighter tabular-nums font-[Outfit] transition-all duration-300" data-testid="calc-total">
            {(!isAdmin && !priceReady) || loading || propertyLoading
              ? <Loader2 className="w-10 h-10 animate-spin inline" data-testid="calc-total-loading" />
              : `$${calc?.total?.toFixed(2) ?? "—"}`}
          </div>
          {!isAdmin && !priceReady && (
            <div className="mt-2 text-[13px] text-emerald-100/85 font-semibold" data-testid="calc-total-waiting">
              {propertyLoading
                ? "Verifying your property…"
                : loading
                  ? "Calculating your custom price…"
                  : property?.formatted_address
                    ? "Waiting for property verification…"
                    : "Enter your address above to get started"}
            </div>
          )}
          <div className="mt-1 text-sm text-emerald-100/80">
            {calc?.service_name}
            {isAdmin ? ` · ${sqft.toLocaleString()} sqft · ${calc?.yard_area_label || "Full yard"}` : ` · ${calc?.frequency_label || "Recurring"} · ${calc?.yard_area_label || "Full yard"}`}
          </div>
          {calc?.frequency_delta !== 0 && calc?.frequency_label && (
            <div className="mt-2 text-[12px] text-emerald-100/70" data-testid="calc-frequency-note">
              {calc.frequency_delta < 0 ? `You save ${Math.abs(calc.frequency_delta * 100).toFixed(0)}% by going recurring` : `Includes ${(calc.frequency_delta * 100).toFixed(0)}% frequency adjustment`}
            </div>
          )}
          <div className="mt-5 flex flex-col gap-2.5">
            {/* iter116: Two-button primary CTA. "Continue" → Book Step 2 order summary.
                "Add More Services" → same route but with ?add=1 so Book auto-opens the picker. */}
            {!compact && (
              <>
                <Button
                  disabled={!priceReady}
                  onClick={() => {
                    const areaAlreadyScaled = autoFilled && !adminOverride;
                    const overrideNum = parseFloat(overgrownOverride);
                    const estimatePayload = {
                      serviceKey, sqft, opts, frequency, yardArea, calc,
                      areaAlreadyScaled,
                      grassHeight, grassCoverage,
                      overgrownOverride,
                      gate_width: gateWidth,
                      overgrown_override_pct: !isNaN(overrideNum) && overrideNum >= 0 ? overrideNum / 100 : null,
                      property: property ? {
                        formatted_address: property.formatted_address || null,
                        lat: property.lat ?? null,
                        lng: property.lng ?? null,
                        place_id: property.place_id || null,
                        square_feet: property.square_feet || null,
                        mowable_sqft: property.mowable_sqft || property.square_feet || null,
                        acres: property.acres || null,
                        building_footprint: property.building_footprint || null,
                        static_map_url: property.static_map_url || null,
                        source: property.source || null,
                      } : null,
                      savedAt: Date.now(),
                    };
                    try {
                      sessionStorage.setItem("jdsn_pending_estimate", JSON.stringify(estimatePayload));
                    } catch { /* private-mode / quota — Book.jsx will fall back to URL recalc */ }
                    const optsFlags = Object.entries(opts).filter(([_,v])=>v).map(([k])=>`${k}=1`).join("&");
                    // iter146 — thread mowable_sqft through the deep-link URL
                    // so Book.jsx's refresh-recompute matches the Estimate
                    // page price exactly (mowable is what drives lawn_mowing
                    // interpolation, not lot sqft).
                    const mowable = (property?.mowable_sqft && property.mowable_sqft > 0) ? property.mowable_sqft : null;
                    const mowableQs = mowable ? `&mowable_sqft=${mowable}` : "";
                    const permalink = `/book/legacy?service=${serviceKey}&sqft=${sqft}${mowableQs}&frequency=${frequency}&yard_area=${yardArea}&grass_height=${grassHeight}&grass_coverage=${grassCoverage}&area_already_scaled=${areaAlreadyScaled ? 1 : 0}${gateWidth ? `&gate_width=${gateWidth}` : ""}${optsFlags ? "&" + optsFlags : ""}`;
                    navigate(permalink);
                  }}
                  className="rounded-full bg-white hover:bg-emerald-50 text-[#062818] font-extrabold h-12 w-full sm:w-auto sm:min-w-[260px] px-7 shadow-md shadow-black/10 sm:self-start disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="calc-continue-btn"
                >
                  {priceReady ? "Continue →" : (isBusy ? "Calculating…" : "Enter address first")}
                </Button>
                <Button
                  disabled={!priceReady}
                  onClick={() => {
                    const areaAlreadyScaled = autoFilled && !adminOverride;
                    const overrideNum = parseFloat(overgrownOverride);
                    const estimatePayload = {
                      serviceKey, sqft, opts, frequency, yardArea, calc,
                      areaAlreadyScaled,
                      grassHeight, grassCoverage,
                      overgrownOverride,
                      gate_width: gateWidth,
                      overgrown_override_pct: !isNaN(overrideNum) && overrideNum >= 0 ? overrideNum / 100 : null,
                      property: property ? {
                        formatted_address: property.formatted_address || null,
                        lat: property.lat ?? null,
                        lng: property.lng ?? null,
                        place_id: property.place_id || null,
                        square_feet: property.square_feet || null,
                        mowable_sqft: property.mowable_sqft || property.square_feet || null,
                        acres: property.acres || null,
                        building_footprint: property.building_footprint || null,
                        static_map_url: property.static_map_url || null,
                        source: property.source || null,
                      } : null,
                      savedAt: Date.now(),
                    };
                    try {
                      sessionStorage.setItem("jdsn_pending_estimate", JSON.stringify(estimatePayload));
                    } catch { /* noop */ }
                    const optsFlags = Object.entries(opts).filter(([_,v])=>v).map(([k])=>`${k}=1`).join("&");
                    // iter146 — mowable_sqft in the URL so refresh-recompute matches.
                    const mowable = (property?.mowable_sqft && property.mowable_sqft > 0) ? property.mowable_sqft : null;
                    const mowableQs = mowable ? `&mowable_sqft=${mowable}` : "";
                    const permalink = `/book/legacy?service=${serviceKey}&sqft=${sqft}${mowableQs}&frequency=${frequency}&yard_area=${yardArea}&grass_height=${grassHeight}&grass_coverage=${grassCoverage}&area_already_scaled=${areaAlreadyScaled ? 1 : 0}${gateWidth ? `&gate_width=${gateWidth}` : ""}${optsFlags ? "&" + optsFlags : ""}&add=1`;
                    navigate(permalink);
                  }}
                  variant="outline"
                  className="rounded-full bg-transparent border-2 border-white/60 hover:bg-white/10 text-white font-extrabold h-12 w-full px-7 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="calc-add-more-services-btn"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Add More Services
                </Button>
              </>
            )}
            {compact && onContinue && (
              <Button
                disabled={!priceReady}
                onClick={() => onContinue({ serviceKey, sqft, opts, frequency, yardArea, areas, calc, areaAlreadyScaled: autoFilled && !adminOverride, grassHeight, grassCoverage, overgrownOverride, gate_width: gateWidth })}
                className="rounded-full bg-white hover:bg-emerald-50 text-[#062818] font-bold h-12 px-7 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="calc-continue-btn"
              >
                {priceReady ? "Continue →" : (isBusy ? "Calculating…" : "Enter address")}
              </Button>
            )}
          </div>
          </>)}
        </div>
      </div>
      </>)}

      <MakeOfferDialog
        open={offerOpen}
        onOpenChange={setOfferOpen}
        estimatedPrice={calc?.total || 0}
        serviceKey={serviceKey}
        squareFeet={sqft}
        frequency={frequency}
        yardArea={yardArea}
        defaultCustomer={{ address: property?.formatted_address || "" }}
        quoteLow={rangeData?.low ?? null}
        quoteHigh={rangeData?.high ?? null}
        quoteOptions={rangeData?.options ?? null}
        quoteDescription={rangeData?.description ?? null}
      />

      {/* BREAKDOWN — admins only. Customers never see size/sqft line items. */}
      {isAdmin && calc?.breakdown?.length > 0 && (
        <div className="rounded-2xl bg-white border border-gray-200 p-5" data-testid="calc-breakdown">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">Breakdown</div>
          <ul className="space-y-2 text-sm text-gray-700">
            {calc.breakdown.map((b, i) => (
              <li key={i} className="flex justify-between">
                <span>{b.label}</span>
                <span className="font-bold tabular-nums text-gray-900">${b.amount.toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CustomerYardSizeCard({ sqft, autoFilled, yardArea }) {
  // iter136 — Customers must NEVER see internal property measurements
  // (sqft, acreage, lot size categories). Only show a verification
  // status pill + the yard area they chose.
  const verified = autoFilled && sqft > 0;
  const areaWord = yardArea === "both" ? "Full yard" : yardArea === "front" ? "Front yard" : "Back yard";
  return (
    <div className="space-y-2" data-testid="customer-yard-size-card">
      <div
        className={`rounded-2xl px-5 py-4 flex items-center gap-3 ${verified ? "bg-emerald-50/70 border-2 border-emerald-200" : "bg-amber-50 border-2 border-amber-200"}`}
        data-testid="customer-yard-category"
      >
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${verified ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>
          {verified ? <ShieldCheck className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">
            {areaWord} · {verified ? "Property Verified" : "Verifying property…"}
          </div>
          <div
            className="mt-0.5 text-lg sm:text-xl font-extrabold tracking-tight font-[Outfit] text-gray-900"
            data-testid="customer-yard-category-label"
          >
            {verified ? "Verified property" : "We're verifying your property"}
          </div>
        </div>
      </div>
      {!verified && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="sqft-pending-verification">
          We&apos;re verifying your property with our records. Please wait a moment while we prepare your custom price.
        </div>
      )}
    </div>
  );
}



function PropertyStat({ label, value, sub }) {
  return (
    <div className="bg-gray-50 rounded-xl border border-gray-100 p-3" data-testid={`property-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-gray-500">{label}</div>
      <div className="mt-0.5 text-sm font-extrabold text-gray-900 tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}
